import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Languages, Headphones } from "lucide-react";

export default function OurTeam() {
  const { ref, isVisible } = useScrollReveal();

  const teamStats = [
    {
      icon: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/user.svg",
      iconType: "image",
      value: "60+",
      label: "Team members",
      color: "#00d115",
      gradient: "linear-gradient(137.738deg, rgb(0, 166, 86) 2.173%, rgb(9, 145, 41) 52.175%, rgb(18, 126, 0) 97.827%)",
    },
    {
      icon: Languages,
      iconType: "component",
      value: "10+",
      label: "Languages Spoken",
      color: "#9c44e9",
      gradient: "linear-gradient(137.738deg, rgb(167, 139, 250) 2.173%, rgb(139, 92, 246) 97.827%)",
    },
    {
      icon: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/map.svg",
      iconType: "image",
      value: "10+",
      label: "Countries",
      color: "#00b0ff",
      gradient: "linear-gradient(137.738deg, rgb(96, 165, 250) 2.173%, rgb(59, 130, 246) 97.827%)",
    },
    {
      icon: Headphones,
      iconType: "component",
      value: "24/7",
      label: "Support Coverage",
      color: "#c06cd7",
      gradient: "linear-gradient(137.738deg, rgb(192, 108, 215) 2.173%, rgb(171, 68, 218) 97.827%)",
    },
  ];

  const locations = [
    ["Dubai, UAE", "Alger, Algeria", "Taipei, Taiwan", "Hanoi, Vietnam"],
    ["Shenzhen, China", "Kansas City, USA", "Metro Manila, Philipines", "New York, USA"],
  ];

  return (
    <div
      ref={ref}
      className={`${
        isVisible ? "" : ""
      } bg-white py-20 lg:py-32 px-6 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-16`}
    >
      <div className="mx-auto space-y-16 max-w-7xl">
        {/* Header Section */}
        <div className="flex flex-col items-center gap-[30px] lg:gap-6 text-center">
          <p className="text-[#757575] text-[18px] xxs:text-[15px] xs:text-[17px] sm:text-[17px] md:text-[18px] 4xl:text-[24px] lg:text-lg font-medium enfinity-subtext-font">The team behind the success</p>
          
          <h1 className="text-[80px] lg:text-[80px] xxs:text-[55px] 4xl:text-[90px] font-normal text-black leading-[106px] xxs:leading-tight xs:leading-tight sm:leading-tight md:leading-tight lg:leading-[106px] enfinity-header-font">
            <span>A </span>
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #00d115, #00b0ff, #9c44e9)",
              }}
            >
              global and diverse
            </span>
            <span> team</span>
          </h1>

          <p className="text-[24px] xxs:text-[1rem] lg:text-2xl font-normal text-black max-w-[728px] leading-[34px] enfinity-subtext-font">
            United by passion, driven by results. Our team brings together expertise from across the globe to serve creators everywhere.
          </p>
        </div>

        {/* Team Stats Cards */}
        <div className="flex flex-col lg:flex-row gap-[29px] justify-center w-full">
          {teamStats.map((stat, index) => (
            <div
              key={index}
              className="bg-white border border-[#eee] rounded-[20px] h-[260px] lg:h-[328px] w-full lg:flex-1 flex flex-col items-center justify-center gap-4 lg:gap-6 shadow-[0px_2px_6px_0px_rgba(144,146,148,0.05)] min-w-0"
            >
              <div
                className="w-[80px] h-[80px] lg:w-[90px] lg:h-[90px] rounded-[20px] flex items-center justify-center"
                style={{ backgroundImage: stat.gradient }}
              >
                {stat.iconType === "image" ? (
                  <img src={stat.icon} alt="" className="w-[32px] h-[32px] lg:w-[40px] lg:h-[40px]" />
                ) : (
                  <stat.icon className="w-[32px] h-[32px] lg:w-[40px] lg:h-[40px] text-white" strokeWidth={1} />
                )}
              </div>
              <p
                className="text-[36px] lg:text-[48px] font-normal text-center"
                style={{ color: stat.color }}
              >
                {stat.value}
              </p>
              <p className="text-lg lg:text-[22px] font-normal text-black text-center leading-[28px] lg:leading-[32px] enfinity-subtext-font">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* Diversity Section */}
        <div className="bg-[#1a1a1a] border border-[#eee] rounded-[20px] overflow-hidden relative h-[600px] xxs:h-[1100px] xs:h-[1100px] sm:h-[1100px] lg:h-[770px] xl:h-[720px] 1xl:h-[750px] 2xl:h-[750px] 4xl:h-[750px] flex flex-col items-center">
          {/* World Image Background */}
          <div className="absolute inset-0 flex items-end justify-center">
            <img
              src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/world.png"
              alt="World Map"
              className="w-[1659px] lg:h-auto xxs:h-[270px] xs:h-[330px] sm:h-[380px] md:h-[600px] object-cover pointer-events-none"
            />
          </div>

          {/* Content Overlay */}
          <div className="relative z-10 flex flex-col items-center gap-8 pt-[110px] px-0 w-full max-w-full">
            <div className="w-full max-w-full flex flex-col items-center">
              <h2 className="text-5xl xxs:text-[55px] lg:text-[80px] 4xl:text-[90px] font-normal text-white text-center w-full max-w-full enfinity-header-font xxs:-mt-12 xs:-mt-12 sm:-mt-12 md:-mt-6 lg:-mt-6 xl:-mt-6 1xl:-mt-3 2xl:-mt-0 4xl:-mt-2 px-6">
                Diversity is our <span className="bg-gradient-to-r from-[#00A656] via-[#72BCD4] to-[#8B5CF6] bg-clip-text text-transparent">Strength</span>
              </h2>

              <p className="text-2xl font-normal text-white text-center xxs:text-[1rem] 4xl:text-xljb lg:max-w-[854px] leading-[34px] w-full max-w-full enfinity-subtext-font xxs:pt-12 xs:pt-12 sm:pt-12 md:pt-12 lg:pt-10 4xl:pt-12 px-6 xl:pt-10">
                Our team spans continents, cultures, and time zones. This global perspective allows us to understand and serve creators from every corner of the world, providing 24/7 support in multiple languages.
              </p>
            </div>

            {/* Location Pills */}
            <div className="flex flex-col gap-[30px] mt-8 lg:mt-16 w-full max-w-full overflow-hidden">
              <style>{`
                @keyframes scroll {
                  0% {
                    transform: translateX(0);
                  }
                  100% {
                    transform: translateX(-50%);
                  }
                }
                .scroll-container {
                  overflow: hidden;
                  width: 100%;
                  max-width: 100%;
                }
                .scroll-content {
                  display: flex;
                  gap: 30px;
                  animation: scroll 30s linear infinite;
                  width: fit-content;
                }
                .scroll-content:hover {
                  animation-play-state: paused;
                }
                @media (min-width: 1024px) {
                  .scroll-container {
                    overflow: visible;
                    width: 100%;
                    max-width: 100%;
                  }
                  .scroll-content {
                    animation: none;
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 30px;
                    width: 100%;
                  }
                }
              `}</style>
              {locations.map((row, rowIndex) => (
                <div key={rowIndex} className="scroll-container lg:overflow-visible 4xl:overflow-visible w-full max-w-full enfinity-subtext-font">
                  <div className="scroll-content lg:flex lg:flex-wrap lg:justify-center lg:gap-[30px] 4xl:flex 4xl:flex-wrap 4xl:justify-center 4xl:gap-[30px]">
                    {/* Duplicate items for seamless loop on mobile only */}
                    {[...row, ...row].map((location, locIndex) => {
                      // Hide duplicates on desktop (lg and above) - show only first set of items
                      const isDuplicate = locIndex >= row.length;
                      return (
                        <div
                          key={locIndex}
                          className={`bg-white border border-[#dadada] rounded-[100px] px-[25px] py-[10px] lg:px-[18px] lg:py-[7px] xl:px-[25px] xl:py-[10px] 4xl:px-[25px] 4xl:py-[10px] flex items-center gap-5 lg:gap-4 xl:gap-5 4xl:gap-5 flex-shrink-0 ${isDuplicate ? 'lg:hidden' : ''}`}
                        >
                          <div className="w-[30px] h-[30px] lg:w-[24px] lg:h-[24px] xl:w-[30px] xl:h-[30px] 4xl:w-[30px] 4xl:h-[30px] flex items-center justify-center">
                            <img src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/location.svg" alt="Location" className="w-[30px] h-[30px] lg:w-[24px] lg:h-[24px] xl:w-[30px] xl:h-[30px] 4xl:w-[30px] 4xl:h-[30px]" />
                          </div>
                          <p className="text-2xl lg:text-lg xl:text-2xl 4xl:text-2xl font-medium text-black whitespace-nowrap">
                            {location}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
